<template>
  <!-- 公共辅料信息面板：展示商品当前绑定的辅料列表 -->
  <div class="material-info-panel">
    <!-- 面板标题 -->
    <div class="panel-title">{{ $t("message.materialInfo") }}</div>
    <!-- 有数据时按卡片展示每条辅料信息 -->
    <div v-if="list.length" class="material-list">
      <div v-for="(item, index) in list" :key="item.material_id || index" class="material-card">
        <!-- 辅料主图，支持点击预览原图 -->
        <div class="material-image">
          <el-image v-if="item.main_image" :src="item.main_image" fit="cover"
            :preview-src-list="[item.main_image]" />
        </div>
        <!-- 辅料名称、状态、单套消耗量和预计消耗 -->
        <div class="material-content">
          <el-tooltip :content="item.name_trans" placement="top" :disabled="!item.name_trans">
            <div class="material-name" @click.stop="openMaterialDetail(item)">{{ item.name_trans }}</div>
          </el-tooltip>
          <el-tag size="mini" effect="plain">{{ item.type_name || (item.type && item.type.name) }}</el-tag>
          <!-- 可编辑时点击编辑图标打开消耗点数修改弹窗 -->
          <div class="material-row consume-row">
            <span>{{ $t("message.materialConsumption") }}：</span>
            <span>{{ item.consume }} {{ $t("message.pointsPerSet") }}</span>
            <i v-if="editable" class="el-icon-edit-outline edit-icon" @click="editConsume(item, index)"></i>
          </div>
          <div class="material-row expected-consumption-row">
            <span>{{ $t("message.expectedConsumption") }}：{{ getExpectedConsumption(item) }}</span>
            <!-- 打开浮层时按需查询，库存信息不占用卡片空间。 -->
            <el-popover placement="bottom" width="180" trigger="click" class="stock-location-control"
              popper-class="material-stock-popover"
              @show="queryStockGrids(item)">
              <div v-loading="stockGridsLoading[item.material_id]" class="material-stock">
                <template v-if="materialStockGrids[item.material_id]">
                  <div class="material-stock-total">
                    <span class="stock-total-label">{{ $t('message.materialCurrentStock') }}</span>
                    <span class="stock-total-value">{{ materialStockGrids[item.material_id].stock_num }}</span>
                  </div>
                  <div v-for="(grid, gridIndex) in materialStockGrids[item.material_id].stock_grids" :key="gridIndex"
                    class="stock-grid-row">
                    <span class="stock-grid-name">
                      <i class="el-icon-location-outline" aria-hidden="true"></i>{{ getStockGridNumber(grid.grid_number) }}
                    </span>
                    <span class="stock-grid-num">{{ grid.num }}</span>
                  </div>
                  <div v-if="!materialStockGrids[item.material_id].stock_grids.length" class="stock-grid-empty">
                    {{ $t('message.noMaterialStockGrids') }}
                  </div>
                </template>
              </div>
              <el-button slot="reference" type="text" size="mini" class="stock-query-button"
                :loading="stockGridsLoading[item.material_id]">{{ $t('message.queryMaterialStockGrids') }}</el-button>
            </el-popover>
          </div>
        </div>
        <!-- 可删除时先进行解除绑定二次确认 -->
        <i v-if="removable" class="el-icon-error remove-icon" @click="confirmRemove(index)"></i>
      </div>
    </div>
    <!-- 无绑定辅料时展示空状态 -->
    <div v-else class="empty">{{ $t("message.noBoundMaterial") }}</div>

    <!-- 第一步：编辑当前辅料的消耗点数 -->
    <el-dialog :title="$t('message.materialConsumption')" :visible.sync="consumeDialogVisible" width="450px"
      append-to-body :close-on-click-modal="false" @closed="resetConsumeDialog">
      <div class="consume-dialog-row">
        <span>{{ $t('message.materialConsumption') }}：</span>
        <el-input ref="consumeInput" v-model="draftConsume" size="small"
          @keyup.enter.native="confirmConsume" />
        <span>{{ $t('message.pointsPerSet') }}</span>
      </div>
      <!-- 第二步：点击确定后继续弹出业务确认提示 -->
      <div slot="footer" class="dialog-footer">
        <el-button @click="consumeDialogVisible = false">{{ $t('message.cancel') }}</el-button>
        <el-button type="primary" @click="confirmConsume">{{ $t('message.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 公共辅料信息卡片。
 * 预计消耗根据辅料单套消耗点数和商品下单数量实时计算。
 * 修改消耗数和解除绑定均在组件内二次确认；确认后再向使用页面抛出事件。
 */
export default {
  name: 'MaterialInfo',
  props: {
    // 辅料列表，仅提供安全默认值，不做字段类型和内容校验
    list: {
      default: () => []
    },
    // 当前商品下单数量，用于实时计算每条辅料的预计消耗
    orderQuantity: {
      default: 0
    },
    // 跳转辅料详情时使用的客户 ID
    userId: {
      default: ''
    },
    // 是否显示消耗编辑入口
    editable: {
      default: true
    },
    // 是否显示删除入口
    removable: {
      default: true
    }
  },
  data() {
    return {
      consumeDialogVisible: false,
      editingIndex: -1,
      draftConsume: '',
      materialStockGrids: {},
      stockGridsLoading: {}
    }
  },
  methods: {
    // 库位只展示“】-”后的实际编号，无此前缀时保留原编号。
    getStockGridNumber(gridNumber) {
      if (!gridNumber) return ''
      const separatorIndex = gridNumber.indexOf('】-')
      return separatorIndex === -1 ? gridNumber : gridNumber.slice(separatorIndex + 2)
    },
    // 只接受辅料主档 ID；同一辅料查询期间阻止重复请求，查完可再次点击刷新。
    async queryStockGrids(item) {
      const materialId = Number(item.material_id)
      if (!Number.isInteger(materialId) || materialId <= 0) {
        this.$message.error(this.$t('message.materialMasterIdRequired'))
        return
      }
      if (this.stockGridsLoading[materialId]) return
      this.$set(this.stockGridsLoading, materialId, true)
      try {
        const res = await this.$api.warehouseApi.materialStockGrids({ material_id: materialId })
        if (res.code !== 0) return
        this.$set(this.materialStockGrids, materialId, res.data)
      } catch (error) {
        // 接口错误由公共请求层提示，保留已查到的库存信息供查看。
      } finally {
        this.$set(this.stockGridsLoading, materialId, false)
      }
    },
    // 点击辅料名称新开辅料详情
    openMaterialDetail(item) {
      const id = item.material_id || item.id
      if (!id) return
      const route = this.$router.resolve({
        path: '/materialDetail',
        query: {
          id,
          user_id: this.userId || item.user_id || ''
        }
      })
      window.open(route.href, '_blank')
    },
    getExpectedConsumption(item) {
      const consume = Number(item.consume)
      const orderQuantity = Number(this.orderQuantity)
      if (!Number.isFinite(consume) || !Number.isFinite(orderQuantity)) return 0
      return consume * orderQuantity
    },
    // 打开消耗点数编辑弹窗，使用草稿值避免直接修改列表
    editConsume(item, index) {
      this.editingIndex = index
      this.draftConsume = item.consume
      this.consumeDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.consumeInput) this.$refs.consumeInput.focus()
      })
    },
    // 变更点数前再次确认，确认后才由使用页面更新数据
    confirmConsume() {
      const index = this.editingIndex
      const item = this.list[index]
      if (!item) return
      if (String(item.consume) === String(this.draftConsume)) {
        this.consumeDialogVisible = false
        return
      }
      const consume = this.draftConsume
      this.consumeDialogVisible = false
      this.$confirm(
        this.$t('message.confirmChangeMaterialConsume'),
        this.$t('message.prompt'),
        {
          confirmButtonText: this.$t('message.ok'),
          cancelButtonText: this.$t('message.cancel'),
          type: 'warning'
        }
      ).then(() => {
        this.$emit('update-consume', { index, consume })
      }).catch(() => {})
    },
    // 解除绑定前再次确认，确认后才由使用页面删除辅料
    confirmRemove(index) {
      this.$confirm(
        this.$t('message.confirmUnbindMaterial'),
        this.$t('message.prompt'),
        {
          confirmButtonText: this.$t('message.ok'),
          cancelButtonText: this.$t('message.cancel'),
          type: 'warning'
        }
      ).then(() => {
        this.$emit('remove', index)
      }).catch(() => {})
    },
    // 编辑弹窗关闭后清理本次草稿状态
    resetConsumeDialog() {
      this.editingIndex = -1
      this.draftConsume = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.material-info-panel {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #edeff4;
  background-color: #fff;
  box-sizing: border-box;
}

.panel-title {
  margin-bottom: 10px;
  color: #333;
  font-size: 16px;
  font-weight: bold;
  line-height: 18px;
}

.material-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  align-items: start;
}

.material-card {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 108px;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background-color: #fff;
  box-sizing: border-box;
}

.material-image {
  flex: 0 0 76px;
  width: 76px;
  height: 76px;
  margin-right: 10px;
  border-radius: 4px;
  background-color: #9b9b9b;
  overflow: hidden;

  .el-image {
    width: 100%;
    height: 100%;
  }
}

.material-content {
  flex: 1;
  min-width: 0;
  padding-right: 2px;
  color: #606266;
  font-size: 12px;
  line-height: 18px;
}

.material-name {
  margin-bottom: 3px;
  color: #303133;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: #c8383e;
  }
}

.el-tag {
  display: block;
  width: fit-content;
  max-width: 100%;
  height: 18px;
  margin-bottom: 3px;
  padding: 0 5px;
  border-color: #d9ecff;
  border-radius: 2px;
  background-color: #ecf5ff;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-row {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 18px;
  line-height: 18px;
}

.consume-row {
  white-space: nowrap;
}

.expected-consumption-row {
  justify-content: space-between;
  column-gap: 8px;

  > span:first-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .stock-location-control {
    flex: none;
    margin-left: auto;
  }
}

.stock-query-button {
  padding: 0;
  font-size: 12px;
  line-height: 18px;
}

// 浮层逐行展示库位，数量较多时在浮层内滚动。
.material-stock {
  min-height: 40px;
  max-height: 240px;
  overflow-y: auto;
  color: #606266;
  font-size: 14px;
  line-height: 22px;
  word-break: break-all;
}

.material-stock-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
  line-height: 24px;

  .stock-total-label {
    color: #909399;
    font-size: 13px;
  }

  .stock-total-value {
    color: #b4272d;
    font-size: 20px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
}

.stock-grid-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 4px;
  background: #f6f7fa;
}

.stock-grid-name {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #303133;

  i {
    flex: none;
    color: #909399;
  }
}

.stock-grid-num {
  flex: none;
  min-width: 24px;
  padding: 0 5px;
  border-radius: 3px;
  color: #b4272d;
  background: #fbeff0;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

.stock-grid-empty {
  padding: 8px 0 2px;
  color: #909399;
  font-size: 12px;
  text-align: center;
}

.consume-dialog-row {
  display: flex;
  align-items: center;
  justify-content: center;

  .el-input {
    width: 180px;
    margin: 0 10px;
  }
}

.dialog-footer {
  text-align: center;
}

.edit-icon {
  flex: none;
  margin-left: 4px;
  color: #b4272d;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    color: #d1494f;
  }
}

.remove-icon {
  position: absolute;
  top: -7px;
  right: -7px;
  z-index: 1;
  color: #c8383e;
  font-size: 18px;
  cursor: pointer;
  background-color: #fff;
  border-radius: 50%;

  &:hover {
    color: #e05258;
  }
}

.empty {
  padding: 16px 0;
  color: #909399;
  text-align: center;
}
</style>

<!-- 库位浮层挂载到 body，使用独立类名限定外层样式。 -->
<style lang="scss">
.material-stock-popover {
  padding: 12px;
  border-color: #e7e9ef;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}
</style>
